<script lang="ts">
	import { fade } from 'svelte/transition'
	import 'overlayscrollbars/styles/overlayscrollbars.css'
	import { OverlayScrollbar } from '@repo/commons/components'
	import { scaleFade } from '@repo/commons/utils'

	interface Props {
		visible?: boolean
		title?: string
		onclose?: (() => void) | null
		children?: import('svelte').Snippet
	}

	let { visible = false, title = '', onclose = null, children }: Props = $props()

	function handleClose() {
		onclose?.()
	}

	function handleBackdropClick() {
		handleClose()
	}

	$effect(() => {
		if (visible) {
			function handleKey(event: KeyboardEvent) {
				if (event.key === 'Escape') {
					handleClose()
				}
			}

			document.addEventListener('keydown', handleKey)

			return () => {
				document.removeEventListener('keydown', handleKey)
			}
		}
	})
</script>

{#if visible}
	<div class="modal-wrapper" aria-hidden={!visible}>
		<div
			class="modal-backdrop"
			role="presentation"
			onclick={handleBackdropClick}
			transition:fade={{ duration: 200 }}
		></div>

		<div
			role="dialog"
			aria-modal="true"
			aria-labelledby="modal-title"
			class={`modal`}
			transition:scaleFade={{ duration: 300, baseScale: 0.95 }}
		>
			<div class="modal-header">
				<h2 id="modal-title" class="modal-title">
					{title}
				</h2>
			</div>

			<OverlayScrollbar>
				<div class="modal-content">
					{@render children?.()}
				</div>
			</OverlayScrollbar>
		</div>
	</div>
{/if}

<style>
	.modal-wrapper {
		@apply absolute top-0 left-0 right-0 bottom-0 z-50 flex justify-center items-center;
	}

	.modal-backdrop {
		@apply absolute top-0 left-0 right-0 bottom-0 bg-[#002C4D] bg-opacity-60;
	}

	.modal {
		@apply relative bg-white flex flex-col outline-none p-8 w-[calc(100%-40px)] max-w-[60vw] max-h-[80vh] rounded-lg border border-[rgba(0,58,102,0.1)] shadow-[0px_8px_8px_-4px_rgba(0,0,0,0.04),0px_20px_24px_-4px_rgba(0,0,0,0.08)];
	}

	.modal-header {
		@apply flex items-center justify-between mb-3;
	}

	.modal-title {
		@apply text-[24px] font-semibold gap-[10px];
	}

	.modal-content {
		@apply flex-grow;
	}

	.modal-content::-webkit-scrollbar {
		width: 6px;
	}

	.modal-content::-webkit-scrollbar-track {
		background: transparent;
	}

	.modal-content::-webkit-scrollbar-thumb {
		@apply bg-brand border-2;
	}
</style>
